import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { DateTime } from "luxon";
import API_URL from "../config";

const GestionarResumenCitas = () => {
  const [programaciones, setProgramaciones] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [servicios, setServicios] = useState([]);

  const [fechaHora, setFechaHora] = useState("");
  const [modo, setModo] = useState("todos");
  const [profesoresSeleccionados, setProfesoresSeleccionados] = useState([]);
  const [servicioSeleccionado, setServicioSeleccionado] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);

  // ===== "Enviar ahora" — sección aparte, no toca las programaciones =====
  const [modoEnviarAhora, setModoEnviarAhora] = useState("todos");
  const [profesoresEnviarAhora, setProfesoresEnviarAhora] = useState([]);
  const [servicioEnviarAhora, setServicioEnviarAhora] = useState("");
  const [enviandoAhora, setEnviandoAhora] = useState(false);

  const cargarProgramaciones = async () => {
    const res = await axios.get(`${API_URL}/api/programaciones-resumen-citas`);
    setProgramaciones(res.data || []);
  };

  const cargarProfesores = async () => {
    const res = await axios.get(`${API_URL}/api/profesores`);
    setProfesores(res.data || []);
  };

  const cargarServicios = async () => {
    const res = await axios.get(`${API_URL}/api/servicios`);
    setServicios((res.data || []).filter((s) => s.habilitado === 1));
  };

  useEffect(() => {
    cargarProgramaciones();
    cargarProfesores();
    cargarServicios();
    cargarEtiquetas();
  }, []);

  const toggleProfesor = (id) => {
    setProfesoresSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  // Atajo: marcar de un clic a todos los profesores activos que sean
  // guía de algún grado (mismo dato que ya se usa en Gestionar Horarios).
  const seleccionarGuiasDeGrado = () => {
    const ids = profesores.filter((p) => p.activo && p.grado_guia_id).map((p) => p.id);
    if (ids.length === 0) {
      alert("⚠️ No hay profesores activos marcados como guía de grado.");
      return;
    }
    setProfesoresSeleccionados(ids);
  };

  // Atajo por etiqueta(s) — mismo patrón que en Gestionar Horarios: al
  // marcar/desmarcar una etiqueta en el combo, se actualiza EN VIVO la
  // lista de profesores marcados, sin botón aparte.
  const [etiquetas, setEtiquetas] = useState([]);
  const [etiquetasSeleccionadas, setEtiquetasSeleccionadas] = useState([]);
  const [etiquetaDropdownAbierto, setEtiquetaDropdownAbierto] = useState(false);
  const etiquetaDropdownRef = useRef(null);

  const cargarEtiquetas = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/etiquetas-docentes`);
      setEtiquetas(res.data || []);
    } catch (err) {
      console.error("Error cargando etiquetas", err);
    }
  };

  useEffect(() => {
    if (!etiquetaDropdownAbierto) return;
    const cerrarSiEsAfuera = (e) => {
      if (etiquetaDropdownRef.current && !etiquetaDropdownRef.current.contains(e.target)) {
        setEtiquetaDropdownAbierto(false);
      }
    };
    document.addEventListener("mousedown", cerrarSiEsAfuera);
    return () => document.removeEventListener("mousedown", cerrarSiEsAfuera);
  }, [etiquetaDropdownAbierto]);

  const aplicarSeleccionPorEtiquetas = (idsEtiquetas) => {
    const ids = profesores
      .filter((p) => p.activo && idsEtiquetas.some((id) => String(id) === String(p.etiqueta_id)))
      .map((p) => p.id);
    setProfesoresSeleccionados(ids);
  };

  const alternarEtiqueta = (id) => {
    setEtiquetasSeleccionadas((prev) => {
      const nuevo = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      aplicarSeleccionPorEtiquetas(nuevo);
      return nuevo;
    });
  };

  const alternarTodasLasEtiquetas = () => {
    const yaEstanTodas = etiquetas.length > 0 && etiquetas.every((e) => etiquetasSeleccionadas.includes(e.id));
    const nuevo = yaEstanTodas ? [] : etiquetas.map((e) => e.id);
    setEtiquetasSeleccionadas(nuevo);
    aplicarSeleccionPorEtiquetas(nuevo);
  };

  const textoEtiquetasSeleccionadas = () => {
    if (etiquetasSeleccionadas.length === 0) return "Por etiqueta...";
    if (etiquetas.length > 0 && etiquetas.every((e) => etiquetasSeleccionadas.includes(e.id))) {
      return "🏷️ Todas las etiquetas";
    }
    return etiquetas
      .filter((e) => etiquetasSeleccionadas.includes(e.id))
      .map((e) => e.nombre)
      .join(", ");
  };

  const limpiarFormulario = () => {
    setEditandoId(null);
    setFechaHora("");
    setModo("todos");
    setProfesoresSeleccionados([]);
    setEtiquetasSeleccionadas([]);
    setServicioSeleccionado("");
  };

  const iniciarEdicion = (prog) => {
    setEditandoId(prog.id);
    setFechaHora(DateTime.fromISO(prog.fecha_hora_envio).toFormat("yyyy-MM-dd'T'HH:mm"));
    setModo(prog.modo_destinatarios);
    setServicioSeleccionado(prog.servicio_id || "");
    setProfesoresSeleccionados(prog.profesor_ids || []);
    setEtiquetasSeleccionadas([]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const guardarProgramacion = async () => {
    if (!fechaHora) {
      alert("Elige la fecha y hora de envío");
      return;
    }
    if (modo === "profesores" && profesoresSeleccionados.length === 0) {
      alert("Selecciona al menos un profesor");
      return;
    }
    if (modo === "servicio" && !servicioSeleccionado) {
      alert("Selecciona un servicio");
      return;
    }

    try {
      setGuardando(true);

      const etiquetasNombres =
        etiquetasSeleccionadas.length > 0
          ? etiquetas.filter((e) => etiquetasSeleccionadas.includes(e.id)).map((e) => e.nombre).join(", ")
          : null;

      const payload = {
        fecha_hora_envio: fechaHora,
        modo_destinatarios: modo,
        profesor_ids: profesoresSeleccionados,
        servicio_id: servicioSeleccionado || null,
        etiquetas_nombres: etiquetasNombres,
      };

      if (editandoId) {
        await axios.post(`${API_URL}/api/programaciones-resumen-citas/${editandoId}`, payload);
      } else {
        await axios.post(`${API_URL}/api/programaciones-resumen-citas`, payload);
      }

      limpiarFormulario();
      cargarProgramaciones();
      alert(editandoId ? "✅ Programación actualizada correctamente" : "✅ Programación creada correctamente");
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "❌ No se pudo guardar la programación");
    } finally {
      setGuardando(false);
    }
  };

  const eliminarProgramacion = async (id) => {
    if (!window.confirm("¿Eliminar esta programación?")) return;
    try {
      await axios.post(`${API_URL}/api/programaciones-resumen-citas/${id}/eliminar`);
      if (editandoId === id) limpiarFormulario();
      cargarProgramaciones();
    } catch (err) {
      alert(err.response?.data?.message || "❌ No se pudo eliminar");
    }
  };

  // ===== "Enviar ahora" =====
  const toggleProfesorEnviarAhora = (id) => {
    setProfesoresEnviarAhora((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const enviarResumenAhora = async () => {
    if (modoEnviarAhora === "profesores" && profesoresEnviarAhora.length === 0) {
      alert("Selecciona al menos un profesor");
      return;
    }
    if (modoEnviarAhora === "servicio" && !servicioEnviarAhora) {
      alert("Selecciona un servicio");
      return;
    }

    const destinoTexto =
      modoEnviarAhora === "todos"
        ? "a TODOS los profesores activos"
        : modoEnviarAhora === "servicio"
        ? "a los profesores del servicio seleccionado"
        : `a ${profesoresEnviarAhora.length} profesor(es) seleccionado(s)`;

    if (!window.confirm(`¿Enviar el resumen de citas ahora mismo ${destinoTexto}? Se manda de inmediato.`)) return;

    try {
      setEnviandoAhora(true);
      const res = await axios.post(`${API_URL}/api/programaciones-resumen-citas/enviar-ahora`, {
        modo_destinatarios: modoEnviarAhora,
        profesor_ids: profesoresEnviarAhora,
        servicio_id: servicioEnviarAhora || null,
      });

      const { total, enviados, sinCitas, fallidos } = res.data;
      alert(
        `✅ Envío completado.\n` +
        `Enviados: ${enviados}\n` +
        `Sin citas pendientes (no se les mandó nada): ${sinCitas}\n` +
        (fallidos > 0 ? `⚠️ Fallaron: ${fallidos}\n` : "") +
        `Total revisados: ${total}`
      );
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "❌ No se pudo enviar el resumen");
    } finally {
      setEnviandoAhora(false);
    }
  };

  return (
    <div className="p-3 md:p-6 md:max-w-3xl">
      <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-center md:text-left">
        📋 Resumen de Citas por Correo
      </h3>
      <p className="text-sm text-gray-500 mb-6 -mt-2">
        Programa el envío automático de un resumen de citas pendientes a los profesores,
        en la fecha y hora que elijas.
      </p>

      {/* ================= CREAR / EDITAR PROGRAMACIÓN ================= */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 md:p-5 mb-6">
        <h4 className="font-bold text-slate-700 mb-3">
          {editandoId ? "✏️ Editando programación" : "Nueva programación"}
        </h4>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fecha y hora de envío
          </label>
          <input
            type="datetime-local"
            value={fechaHora}
            onChange={(e) => setFechaHora(e.target.value)}
            className="border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ¿A quién se le envía?
          </label>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={modo === "todos"}
                onChange={() => setModo("todos")}
              />
              Todos los profesores activos
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={modo === "servicio"}
                onChange={() => setModo("servicio")}
              />
              Por servicio
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={modo === "profesores"}
                onChange={() => setModo("profesores")}
              />
              Profesores específicos
            </label>
          </div>
        </div>

        {modo === "servicio" && (
          <div className="mb-4">
            <select
              value={servicioSeleccionado}
              onChange={(e) => setServicioSeleccionado(e.target.value)}
              className="border border-gray-300 p-2 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">Selecciona un servicio...</option>
              {servicios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </div>
        )}

        {modo === "profesores" && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={seleccionarGuiasDeGrado}
              className="bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition"
              title="Marca a todos los profesores activos que son guía de algún grado"
            >
              🎓 Guías de grado
            </button>

            {etiquetas.length > 0 && (
              <div ref={etiquetaDropdownRef} style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setEtiquetaDropdownAbierto((v) => !v)}
                  className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                  style={{ maxWidth: 220 }}
                >
                  <span className="truncate">{textoEtiquetasSeleccionadas()}</span>
                  <span>{etiquetaDropdownAbierto ? "▲" : "▼"}</span>
                </button>

                {etiquetaDropdownAbierto && (
                  <div
                    className="absolute z-30 bg-white border border-gray-300 rounded-lg mt-1 shadow-lg"
                    style={{ maxHeight: 220, minWidth: 200, overflowY: "auto", padding: "6px 10px" }}
                  >
                    <label className="flex items-center gap-2 py-1 font-semibold border-b border-gray-100 mb-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={etiquetas.length > 0 && etiquetas.every((e) => etiquetasSeleccionadas.includes(e.id))}
                        onChange={alternarTodasLasEtiquetas}
                      />
                      🏷️ Todas las etiquetas
                    </label>
                    {etiquetas.map((e) => (
                      <label key={e.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={etiquetasSeleccionadas.includes(e.id)}
                          onChange={() => alternarEtiqueta(e.id)}
                        />
                        <span
                          style={{ width: 10, height: 10, borderRadius: 3, background: e.color || "#94a3b8", flexShrink: 0 }}
                        />
                        {e.nombre}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {modo === "profesores" && (
          <div className="mb-4 border border-gray-200 rounded-lg p-3 max-h-56 overflow-y-auto">
            {profesores.map((p) => (
              <label key={p.id} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={profesoresSeleccionados.includes(p.id)}
                  onChange={() => toggleProfesor(p.id)}
                />
                {p.nombre}
              </label>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={guardarProgramacion}
            disabled={guardando}
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold transition"
          >
            {guardando
              ? "Guardando..."
              : editandoId
              ? "💾 Guardar cambios"
              : "➕ Programar envío"}
          </button>

          {editandoId && (
            <button
              onClick={limpiarFormulario}
              disabled={guardando}
              className="bg-gray-200 hover:bg-gray-300 text-slate-700 px-5 py-2 rounded-lg font-semibold transition"
            >
              Cancelar edición
            </button>
          )}
        </div>
      </div>

      {/* ================= ENVIAR RESUMEN AHORA ================= */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 md:p-5 mb-6">
        <h4 className="font-bold text-slate-700 mb-1">📤 Enviar resumen ahora</h4>
        <p className="text-sm text-gray-500 mb-3">
          Manda el resumen de inmediato, sin esperar ninguna fecha programada — útil si un
          profesor te pregunta directamente cuántas citas tiene.
        </p>

        <div className="mb-4">
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={modoEnviarAhora === "todos"}
                onChange={() => setModoEnviarAhora("todos")}
              />
              Todos los profesores activos
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={modoEnviarAhora === "servicio"}
                onChange={() => setModoEnviarAhora("servicio")}
              />
              Por servicio
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={modoEnviarAhora === "profesores"}
                onChange={() => setModoEnviarAhora("profesores")}
              />
              Profesores específicos
            </label>
          </div>
        </div>

        {modoEnviarAhora === "servicio" && (
          <div className="mb-4">
            <select
              value={servicioEnviarAhora}
              onChange={(e) => setServicioEnviarAhora(e.target.value)}
              className="border border-gray-300 p-2 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">Selecciona un servicio...</option>
              {servicios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </div>
        )}

        {modoEnviarAhora === "profesores" && (
          <div className="mb-4 border border-gray-200 rounded-lg p-3 max-h-56 overflow-y-auto">
            {profesores.map((p) => (
              <label key={p.id} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={profesoresEnviarAhora.includes(p.id)}
                  onChange={() => toggleProfesorEnviarAhora(p.id)}
                />
                {p.nombre}
              </label>
            ))}
          </div>
        )}

        <button
          onClick={enviarResumenAhora}
          disabled={enviandoAhora}
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-lg font-semibold transition"
        >
          {enviandoAhora ? "Enviando..." : "📤 Enviar ahora"}
        </button>
      </div>

      {/* ================= LISTA DE PROGRAMACIONES ================= */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 md:p-5">
        <h4 className="font-bold text-slate-700 mb-3">Programaciones</h4>

        {programaciones.length === 0 ? (
          <p className="text-gray-500 text-sm">No hay programaciones creadas.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {programaciones.map((prog) => (
              <div
                key={prog.id}
                className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 bg-slate-50 rounded-lg p-3"
                style={{ borderLeft: `4px solid ${prog.enviado ? "#22c55e" : "#eab308"}` }}
              >
                <div>
                  <p className="text-sm font-bold text-indigo-700 mb-0.5">
                    {prog.modo_destinatarios === "todos"
                      ? "📨 Envío masivo para todos los profesores"
                      : prog.modo_destinatarios === "servicio"
                      ? `📨 Envío masivo para el servicio ${prog.servicio_nombre || "—"}`
                      : prog.etiquetas_nombres
                      ? `📨 Envío masivo para: ${prog.etiquetas_nombres}`
                      : "📨 Envío masivo para profesores específicos"}
                  </p>
                  <p className="font-semibold text-slate-800">
                    {DateTime.fromISO(prog.fecha_hora_envio).toFormat("dd/MM/yyyy hh:mm a")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {prog.modo_destinatarios === "todos"
                      ? "Todos los profesores activos"
                      : prog.modo_destinatarios === "servicio"
                      ? `Servicio: ${prog.servicio_nombre || "—"}`
                      : `Profesores: ${prog.profesores.join(", ") || "—"}`}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs font-bold px-2 py-1 rounded-full ${
                      prog.enviado ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {prog.enviado ? "Enviado" : "Pendiente"}
                  </span>

                  {!prog.enviado && (
                    <button
                      onClick={() => iniciarEdicion(prog)}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition"
                    >
                      ✏️ Editar
                    </button>
                  )}

                  <button
                    onClick={() => eliminarProgramacion(prog.id)}
                    className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold transition"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GestionarResumenCitas;
